import _ from 'lodash';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { useI18n } from "Hooks/useI18n";
import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { getTrainingSubjects, TrainingSubject } from "Components/pages/SecurityTipsPage/Data";
import { useTenantSettings } from "Hooks/useTenantSettings";
import {
  ConnectorUpsertDto,
  FlowSpecDto,
  FlowSpecStatusKind,
  FlowSpecUpsertDto,
  KIND,
  Page,
  AMP_SETTINGS,
  ProviderDto,
  SecurityTipSubject,
  Category,
  CredentialStatus,
  ConnectorStatus
} from "@ampsec/platform-client";
import { useApiObjectData } from "Hooks/useApiObjectData";
import { useApiData } from "Hooks/useApiData";
import { getGridData, getInstalledConnectors, getProvidersData, updateEngagementFlow } from "Apis/library";
import { ERROR_VARIANT, INFO_VARIANT } from "Core-utils/constants/constants";
import { DropdownComponent, ToggleableListItem } from "Components/molecules";
import { Typography } from "Components/atoms";
import { ContentfulContext } from "Rdx/contexts/contentfulContext";
import './styles.scss';
import { Spinner } from 'react-bootstrap';

interface NotificationConnectorItem {
  id: string;
  value: string;
  icon: string;
  credentialStatus: string;
  status: ConnectorStatus;
}

interface SecurityTipsListViewProps {
  disabled?: boolean;
}

const SecurityTipsListView = ({ disabled = false }: SecurityTipsListViewProps) => {
  const { t } = useI18n();
  const [trainingSubjects, setTrainingSubjects] = useState<TrainingSubject[]>([]);
  const [activeSecurityTipSubjects, setActiveSecurityTipSubjects] = useState<string[]>([]);
  // Security tip flow specs are filtered from all flow specs and mapped to the associated training focus
  const [securityTipFlowSpecs, setSecurityTipFlowSpecs] = useState<FlowSpecDto[]>([]);
  // Track which subjects are currently being saved (for per-subject loading state)
  const [savingSubjects, setSavingSubjects] = useState<Set<SecurityTipSubject>>(new Set());
  const [notificationConnectorIsDirty, setNotificationConnectorIsDirty] = useState<boolean | undefined>(undefined);
  const [updatingNotificationConnector, setUpdatingNotificationConnector] = useState<boolean>(false);
  // Track pending settings changes to avoid race conditions
  const pendingSettingsChanges = useRef<Map<SecurityTipSubject, boolean>>(new Map());
  const settingsDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const settingsDebounceDelay = 1250;
  const { tenantSettings, settingsLoading, refreshTenantSettings, getAmpSettingValue, updateAmpSetting } = useTenantSettings();
  const { addToast } = useContext(ToastsContext);

  // Notification connector state
  const [securityTipsNotificationConnector, setSecurityTipsNotificationConnector] = useState<string | undefined>('');
  const [notificationConnectorItems, setNotificationConnectorItems] = useState<NotificationConnectorItem[]>([]);

  // Specify a transformer for flow spec data...
  const flowSpecTransformer = (dataItem: Page<FlowSpecDto>) => {
    return dataItem.data;
  };
  //... retrieve flow specs from the API
  const { data: flowSpecs, refresh: refreshFlowSpecs } = useApiObjectData('flowSpecs', getGridData, flowSpecTransformer, {
    limit: 250,
    status: [FlowSpecStatusKind.ACTIVE, FlowSpecStatusKind.PAUSED],
  });

  // Fetch notification connectors and providers
  const { contentfulData } = useContext(ContentfulContext);
  const integrations = contentfulData?.integrations;
  const { data: installedConnectors } = useApiData<ConnectorUpsertDto, ConnectorUpsertDto>(
    KIND.CONNECTORS,
    () => getInstalledConnectors({ limit: 250 }),
    (a) => a,
  );
  const { data: providersList } = useApiData<ProviderDto, ProviderDto>(
    KIND.PROVIDERS,
    () => getProvidersData({ limit: 250 }),
    (a) => a,
  );

  // Build notification connector options when data is available
  useEffect(() => {
    /*
    // installedConnectors, providersList, integrations, securityTipFlowSpecs
    */
    if (!installedConnectors || !providersList || !integrations || !securityTipFlowSpecs) {
      return;
    }

    const providerDisplayValueToLogoMap = integrations
      ? Object.keys(integrations)?.reduce((acc: { [k: string]: string }, integration: string) => {
          acc[integrations[integration].providerKey] = `https:${integrations[integration]?.logo?.fields?.file?.url}`;
          return acc;
        }, {})
      : {};

    const notificationCategory = (Array.isArray(providersList) ? providersList : [])
      .filter((provider) => provider.categories.includes('NOTIFICATION' as Category))
      .map((provider) => ({
        id: provider.id,
        category: provider.categories,
        value: provider.displayValue,
        icon: providerDisplayValueToLogoMap[provider.displayValue],
      }));

    const connectorsArray = Array.isArray(installedConnectors)
      ? installedConnectors
      : ((Object.values(installedConnectors || {}) as unknown) as ConnectorUpsertDto[]);

    const mapConnectorToCategory = (connector: ConnectorUpsertDto) => {
      const matchingCategory = notificationCategory.find((category) => category.id === connector.pid);
      return matchingCategory
        ? {
            ...connector,
            category: matchingCategory.category,
            displayValue: matchingCategory.value,
            icon: matchingCategory.icon,
          }
        : null;
    };

    const connectors = connectorsArray
      .map(mapConnectorToCategory)
      .filter(Boolean)
      .sort((a, b) => new Date(a?.createdAt ?? '').getTime() - new Date(b?.createdAt ?? '').getTime());

    const activeConnectorItems: NotificationConnectorItem[] = connectors.map((item) => ({
      id: item?.id ?? '',
      value: item?.displayValue ?? '',
      icon: item?.icon ?? '',
      credentialStatus: item?.credentialStatus ?? CredentialStatus.NOT_INSTALLED,
      status: item?.status ?? ConnectorStatus.DISABLED,
    }));
    setNotificationConnectorItems(activeConnectorItems);

   if (connectors.length <= 1) {
    return;
   }
    // Mark initial load as complete after setting the connector
  }, [installedConnectors, providersList, integrations, securityTipFlowSpecs]);

  // #### Settings and Security Tip Subjects Update Side Effects #### //
  // Update the active security tip subjects setting when the tenant settings are loaded
  useEffect(() => {
    if (settingsAreLoading) {
      return;
    }
    const activeSubjects = getAmpSettingValue(AMP_SETTINGS.tenant.SECURITY_TIPS_ACTIVE_SUBJECTS.id) ?? AMP_SETTINGS.tenant.SECURITY_TIPS_ACTIVE_SUBJECTS.defaultValue;
    setActiveSecurityTipSubjects(activeSubjects);
  }, [tenantSettings]);

  useEffect(() => {
    if (!tenantSettings || !notificationConnectorItems || _.isEmpty(notificationConnectorItems)) {
      return;
    }

    const expectedConnectorId = getAmpSettingValue(AMP_SETTINGS.tenant.SECURITY_TIPS_NOTIFICATION_CONNECTOR_ID.id);
    setSecurityTipsNotificationConnector(expectedConnectorId);

  }, [tenantSettings, providersList, installedConnectors, notificationConnectorItems]);

  useEffect(() => {
    if (settingsAreLoading || !notificationConnectorItems || _.isEmpty(notificationConnectorItems)) {
      return;
    }
    const isDirty = evaluateNotificationConnectorIsDirty(securityTipsNotificationConnector);
    if (isDirty) {
      setSecurityTipsNotificationConnector(undefined);
    }
    setNotificationConnectorIsDirty(isDirty);
  }, [securityTipsNotificationConnector]);

  // Once settings and flow specs are loaded, set `securityTipFlowSpecs`
  useEffect(() => {
    if (settingsAreLoading || flowSpecs?.length === 0 || Object.keys(flowSpecs).length === 0) {
      return;
    }
    const tipSpecs = flowSpecs?.filter((flowSpec) => flowSpec.securityTip?.isConfigured === true);
    setSecurityTipFlowSpecs(tipSpecs);
  }, [flowSpecs]);

  useEffect(() => {
    if (settingsAreLoading) {
      return;
    }
    setTrainingSubjects(getTrainingSubjects(t, activeSecurityTipSubjects, securityTipFlowSpecs));
  }, [securityTipFlowSpecs]);

  // finally, once everything is loaded set our training subjects
  useEffect(() => {
    if (savingSubjects.size !== 0 || settingsAreLoading || _.isEmpty(securityTipFlowSpecs) || _.isEmpty(trainingSubjects) || (trainingSubjects.length === 0)) {
      return;
    }
    const updateMap = new Map<SecurityTipSubject, boolean>();
    // Check to ensure all security tip flow spec statuses match the active security tip subjects
    // If a flow spec is not active, update it to the active status using the handleTrainingFocusStatusUpdate function
    for (const flowSpec of securityTipFlowSpecs) {
      if (updateMap.has(flowSpec.securityTip?.subject as SecurityTipSubject)) {
        continue;
      }
      if (activeSecurityTipSubjects.includes(flowSpec.securityTip?.subject as SecurityTipSubject)) {
        if (flowSpec.status === FlowSpecStatusKind.PAUSED) {
          updateMap.set(flowSpec.securityTip?.subject as SecurityTipSubject, true);
        }
      }
      if (!(activeSecurityTipSubjects.includes(flowSpec.securityTip?.subject as SecurityTipSubject))) {
        if (flowSpec.status === FlowSpecStatusKind.ACTIVE) {
          updateMap.set(flowSpec.securityTip?.subject as SecurityTipSubject, false);
        }
      };
    }
    for (const subject of updateMap.keys()) {
      handleTrainingFocusStatusUpdate(subject, updateMap.get(subject) as boolean);
    }
  }, [tenantSettings, activeSecurityTipSubjects, securityTipFlowSpecs, trainingSubjects]);


  // #### End of Settings and Security Tip Subjects Update Side Effects #### //

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (settingsDebounceTimer.current) {
        clearTimeout(settingsDebounceTimer.current);
      }
    };
  }, []);


  // #### Helper Functions #### //

  /**
   * Compares the notification connector setting to those used in Security Tip flow specs, checks if the connector has valid credentials.
   * Returns `true` if the connector setting is dirty (needs updating); `false` otherwise
   * @param connectorId - The connector ID to evaluate
   * @returns `true` if the connector setting is dirty; `false` otherwise
   */
  const evaluateNotificationConnectorIsDirty = (connectorId?: string): boolean => {
    if (!connectorId) {
      return true;
    }
    const flowSpecsMatch = securityTipFlowSpecs.every((flowSpec) => flowSpec.engagementChannelCids.includes(connectorId));
    const matchingConnectorHasValidCredentials = notificationConnectorItems.find((item) => item.id === connectorId)?.credentialStatus === CredentialStatus.VALID;
    return !flowSpecsMatch || !matchingConnectorHasValidCredentials;
  };

  /**
   * Checks if the settings are loading or not present
   * @returns `true` if the settings are loading or not present; `false` otherwise
   */
  const settingsAreLoading = settingsLoading || !tenantSettings || Object.keys(tenantSettings).length === 0;
  /**
   * Converts a FlowSpecDto to FlowSpecUpsertDto for API persistence
   * @param flowSpec - The flow spec to convert
   * @returns A FlowSpecUpsertDto ready to be saved via the API
   */
  const convertToFlowSpecUpsertDto = (flowSpec: FlowSpecDto): FlowSpecUpsertDto => {
    return {
      id: flowSpec.id,
      ...flowSpec.filter,
      name: flowSpec.name,
      filter: { cohorts: [] }, // TODO: add cohorts
      engagementChannelCids: flowSpec.engagementChannelCids, // TODO: support using default engagement channel
      triggerFilter: flowSpec.triggerFilter,
      description: flowSpec.description,
      status: flowSpec.status,
      tid: flowSpec.tid,
      fslStrategy: 'STANDARD',
      securityTip: flowSpec.securityTip,
    };
  };

  /**
   * Saves flow specs for a specific subject to the API
   * @param subject - The security tip subject whose flow specs should be saved
   * @param enabled - The enabled status to set on the flow specs
   * @returns A promise that resolves when all flow specs are saved
   */
  const saveFlowSpecsForSubject = async (subject: SecurityTipSubject, enabled: boolean): Promise<void> => {
    const flowSpecsToSave = securityTipFlowSpecs.filter((flowSpec) => flowSpec.securityTip?.subject === subject);
    if (flowSpecsToSave.length === 0) {
      return;
    }

    // Update the status and convert to DTOs
    const flowSpecDtos = flowSpecsToSave.map((flowSpec) => {
      const updatedFlowSpec = { ...flowSpec, status: enabled ? FlowSpecStatusKind.ACTIVE : FlowSpecStatusKind.PAUSED };
      const dto = convertToFlowSpecUpsertDto(updatedFlowSpec);

      // If enabling and a notification connector is selected, set it as the engagement channel
      if (enabled && securityTipsNotificationConnector) {
        dto.engagementChannelCids = [securityTipsNotificationConnector];
      }

      return dto;
    });

    // Save all flow specs in parallel
    const promises = flowSpecDtos.map((flowSpec) => updateEngagementFlow(flowSpec));
    await Promise.all(promises);
  };

  /**
   * Batches and saves all pending settings changes
   * @returns A promise that resolves when settings are saved
   */
  const commitPendingSettingsChanges = async (): Promise<void> => {
    // Copy the current active security tip subjects into a new set to avoid modifying the original
    const currentActiveSubjects = new Set(activeSecurityTipSubjects);
    pendingSettingsChanges.current.forEach((enabled, subject) => {
      // Compare the enabled status of the subject to the current active subjects
      if (enabled) {
        currentActiveSubjects.add(subject);
      } else {
        currentActiveSubjects.delete(subject);
      }
    });
    // Convert the set back to an array for the API
    const finalSubjects = Array.from(currentActiveSubjects);
    try {
      await updateAmpSetting(AMP_SETTINGS.tenant.SECURITY_TIPS_ACTIVE_SUBJECTS.id, finalSubjects, true);
      // Clear pending changes after successful save
      pendingSettingsChanges.current.clear();
    } catch (error: unknown) {
      throw error;
    }
  };

  /**
   * Processes flow spec updates for all changed subjects after settings are saved
   * @param changedSubjectsMap - Map of subjects to their enabled states
   * @returns Promise that resolves when all flow specs are saved
   */
  const processFlowSpecUpdates = async (changedSubjectsMap: Map<SecurityTipSubject, boolean>): Promise<void> => {
    const flowSpecPromises = Array.from(changedSubjectsMap.entries()).map(([subj, enabled]) => {
      const hasFlowSpecs = trainingSubjects.find((ts) => ts.subject === subj)?.flowSpecs.length ?? 0 > 0;
      if (hasFlowSpecs) {
        return saveFlowSpecsForSubject(subj, enabled);
      }
      return Promise.resolve();
    });

    await Promise.all(flowSpecPromises);
  };

  /**
   * Executes the batched save operation for settings and flow specs
   * @param changedSubjectsMap - Map of subjects to their enabled states
   */
  const executeBatchedSave = async (changedSubjectsMap: Map<SecurityTipSubject, boolean>): Promise<void> => {
    try {
      await commitPendingSettingsChanges();
      await processFlowSpecUpdates(changedSubjectsMap);
      await Promise.all([refreshTenantSettings(), refreshFlowSpecs()]);
      addToast({
        body: t('SECURITY_TIPS_TRAINING_FOCUS_UPDATED'),
        severity: INFO_VARIANT,
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addToast({
        body: `Error updating security tip subjects: ${errorMessage}`,
        severity: ERROR_VARIANT,
      });
    } finally {
      setSavingSubjects(new Set());
    }
  };

  /**
   * Updates the enabled status of a given security tip training subject and persists changes.
   * Uses debounced batching for settings to avoid race conditions.
   * @param subject - The security tip subject to update
   */
  const handleToggleTrainingFocusToggle = (subject: SecurityTipSubject) => async (ev: ChangeEvent<HTMLInputElement>) => {
    const updatedEnabled = ev.target.checked;
    await handleTrainingFocusStatusUpdate(subject, updatedEnabled);
  };

  const handleTrainingFocusStatusUpdate = async (subject: SecurityTipSubject, enabled: boolean) => {
    setSavingSubjects((prev) => new Set(prev).add(subject));

    // Queue the settings change for batched saving and reset the debounce timer
    pendingSettingsChanges.current.set(subject, enabled);
    if (settingsDebounceTimer.current) {
      clearTimeout(settingsDebounceTimer.current);
    }
    settingsDebounceTimer.current = setTimeout(async () => {
      // Clean copy of the pending changes before committing to avoid race conditions
      const changedSubjectsMap = new Map(pendingSettingsChanges.current);
      await executeBatchedSave(changedSubjectsMap);
    }, settingsDebounceDelay);
  };

  const setDefaultNotificationChannelForFlowSpec = async (flowSpec: FlowSpecDto, connectorId?: string): Promise<void> => {
    const connectorToUse = connectorId ?? securityTipsNotificationConnector;
    if (!connectorToUse) {
      return;
    }

    const updatedFlowSpec: FlowSpecUpsertDto = {
      ...convertToFlowSpecUpsertDto(flowSpec),
      engagementChannelCids: [connectorToUse],
    };

    try {
      await updateEngagementFlow(updatedFlowSpec);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addToast({
        body: `Error setting notification channel: ${errorMessage}`,
        severity: ERROR_VARIANT,
      });
      throw error;
    }
  };

  /**
   * Updates the notification channel for all active security tip flow specs
   * Called when the notification connector selection changes
   * @param connectorId - The connector ID to set as the notification channel
   */
  const updateAllActiveFlowSpecsNotificationChannel = async (connectorId: string): Promise<void> => {
    if (securityTipFlowSpecs.length === 0) {
      return;
    }

    try {
      const updatePromises = securityTipFlowSpecs.map((flowSpec) =>
        setDefaultNotificationChannelForFlowSpec(flowSpec, connectorId)
      );
      await Promise.all(updatePromises);
      addToast({
        body: t('SECURITY_TIPS_NOTIFICATION_CHANNEL_UPDATED'),
        severity: INFO_VARIANT,
      });
      await refreshFlowSpecs();
    } catch {
      // Error toast is already shown in setDefaultNotificationChannelForFlowSpec
    }
  };

  // Get the display name of the currently selected connector
  const getSelectedConnectorDisplayName = (): string => {
    const selectedItem = notificationConnectorItems.find(
      (item) => item.id === securityTipsNotificationConnector
    );
    return selectedItem?.value ? `${selectedItem?.value} (Status: ${selectedItem?.credentialStatus ?? ''})` : '';
  };

  // Handle dropdown selection change - receives the display name (with status), extracts and saves the ID
  const handleNotificationConnectorChange = async (eventKey: string | null) => {
    if (!eventKey) {
      return;
    }
    // The eventKey includes the status suffix, so we need to match by checking if it starts with the value
    const selectedItem = notificationConnectorItems.find(
      (item) => eventKey.startsWith(item.value)
    );
    // Don't update the selected connector if it's the same as the current one
    if (selectedItem?.id === securityTipsNotificationConnector) {
      return;
    }
    setUpdatingNotificationConnector(true);
    if (selectedItem?.id) {
      // Directly update all active flow specs with the new connector
      setSecurityTipsNotificationConnector(selectedItem.id);
      const settingKey = AMP_SETTINGS.tenant.SECURITY_TIPS_NOTIFICATION_CONNECTOR_ID.id;
      await updateAmpSetting(settingKey, selectedItem.id, true);
      await updateAllActiveFlowSpecsNotificationChannel(selectedItem.id);
      refreshTenantSettings();
      setUpdatingNotificationConnector(false);
    }
  };

  // Build dropdown items with status display
  const getDropdownItems = () => {
    return notificationConnectorItems.map((item) => ({
      ...item,
      value: item.value ? `${item.value} (Status: ${item.credentialStatus})` : '',
      disabled: item.credentialStatus === 'NOT_INSTALLED',
    }));
  };
  // #### End of Helper Functions #### //

  // #### Render Function #### //
  const focusList = trainingSubjects.map((trainingSubject) => {
    const isLoading = savingSubjects.has(trainingSubject.subject);
    const customDescriptionContent = trainingSubject.previewUrl ? (
      <Typography variant="body3" color="text-medium-emphasis" className="module-description">
        <a target="_blank" href={trainingSubject.previewUrl}>
          {`Preview ${trainingSubject.name} Microtraining Engagement`}
        </a>
      </Typography>
    ) : null;
    return <ToggleableListItem
      key={`training-focus-${trainingSubject.subject}`}
      id={trainingSubject.subject}
      title={trainingSubject.name}
      description={trainingSubject.description}
      checked={trainingSubject.enabled}
      disabled={isLoading || disabled}
      loading={isLoading}
      advancedToggleView={true}
      thumbnailUrl={trainingSubject.thumbnailUrl}
      toggleLabel={`${trainingSubject.enabled ? 'Disable' : 'Enable'} ${t('SECURITY_TIP_TOGGLE_LABEL')}`}
      customDescriptionContent={customDescriptionContent}
      onChange={async (ev: ChangeEvent<HTMLInputElement>) => await handleToggleTrainingFocusToggle(trainingSubject.subject)(ev)}
    />;
  });

  return (
    <div className="d-flex flex-column gap-3">
      {focusList}
      {notificationConnectorItems.length > 0 && (
        <div className={`d-flex flex-row gap-3 mb-3 mt-3`}>
          <div id="security-tips-notification-channel-container" className={`d-flex`}>
            <Typography variant="body2" color="text-high-emphasis" className="d-flex flex-column" style={{ alignSelf: 'center', marginBottom: '0%' }}>
              { notificationConnectorIsDirty ? t('SECURITY_TIPS_NOTIFICATION_CHANNEL_LABEL_NEEDS_UPDATE') : t('SECURITY_TIPS_NOTIFICATION_CHANNEL_LABEL')}
            </Typography>
          </div>
          <div style={{ maxWidth: '300px' }}>
            <DropdownComponent
              value={getSelectedConnectorDisplayName()}
              dropDownItems={getDropdownItems()}
              onChange={handleNotificationConnectorChange}
              placeholder={t('SELECT_NOTIFICATION_CHANNEL_PLACEHOLDER')}
              selectedItemIcon={notificationConnectorItems.find(
                (item) => item.id === securityTipsNotificationConnector
              )?.icon}
              isStartCase={false}
              disableToggle={disabled}
            />
          </div>
          {updatingNotificationConnector && (
            <Spinner
              animation="border"
              className="d-flex flex-column"
              style={{ alignSelf: 'center', marginBottom: '0%',width: '1rem', height: '1rem' }}
              size="sm"
              variant="text-interactive"
              role="status"
              aria-label="Saving..."
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SecurityTipsListView;